import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AllRoutes } from 'constants/routes';
import { useEntitiesTable } from 'hooks/useEntitiesTable';
import { IMonitoredVehicle } from 'types/vehicles-monitoring';
import { useVehiclesMonitoringColumns } from './useVehiclesMonitoringColumns';
import { actions as vehiclesActions } from 'core/redux/api/veta/airsideVehicles';
import { monitoredVehiclesSelector } from 'core/redux/selectors/monitoredVehicles';

export const useVehiclesMonitoring = () => {
  const dispatch = useDispatch();

  const [isFormModal, setFormModal] = useState<boolean>(false);
  const [isConfirmModal, setConfirmModal] = useState<boolean>(false);
  const [selectedVehicle, setSelectedVehicle] = useState<IMonitoredVehicle | null>(null);

  const {
    page,
    pageSize,
    sort,
    search,
    searchParams,
    columnsSorting,
    updatePage,
    updatePageSize,
    sortColumnData,
    searchHandler,
  } = useEntitiesTable(AllRoutes.VehiclesMonitoring);

  const { pending: isLoading, page: vehicles, error } = useSelector(monitoredVehiclesSelector);

  const pageData = { no: page, size: pageSize.value, sort, search: search || undefined };

  const closeModals = () => {
    setFormModal(false);
    setConfirmModal(false);
    setSelectedVehicle(null);
  };

  const openFormModal = () => setFormModal(true);

  const openConfirmModal = () => setConfirmModal(true);

  const fetchVehicles = () => {
    dispatch(vehiclesActions.load.request(pageData));
  };

  const onEdit = (vehicle: IMonitoredVehicle) => {
    setSelectedVehicle(vehicle);
    openFormModal();
  };

  const onDelete = (vehicle: IMonitoredVehicle) => {
    setSelectedVehicle(vehicle);
    openConfirmModal();
  };

  const deleteVehicle = () => {
    if (selectedVehicle) {
      dispatch(vehiclesActions.remove.request({ id: selectedVehicle.id, page: pageData }));
    }
  };

  const columns = useVehiclesMonitoringColumns(onEdit, onDelete);

  const controlProps = { search, searchHandler, openModal: openFormModal };

  const tableProps = { isLoading, data: vehicles?.objects || [], columns, columnsSorting, sortColumnData };

  const footerProps = {
    totalCount: vehicles?.total || 0,
    pageSize,
    page,
    onPageChange: updatePage,
    updatePageSize,
  };

  const formProps = {
    asset: selectedVehicle,
    page: pageData,
    close: closeModals,
  };

  const confirmProps = { close: closeModals, deleteVehicle };

  useEffect(() => {
    fetchVehicles();
  }, [searchParams]);

  useEffect(() => {
    if (vehicles?.objects?.length === 0 && page !== 1) {
      updatePage({ selected: page - 1 });
    }
  }, [vehicles]);

  return { isFormModal, isConfirmModal, controlProps, tableProps, formProps, confirmProps, footerProps };
};
